/**
 * StaffingStep Component - Refactored with Service-Oriented Architecture
 * 
 * This is the PROPERLY refactored version that uses:
 * - Service layer for data management (hooks handle all useEffect patterns)
 * - Custom hooks for business logic (contain proper useEffect implementations)
 * - Modern React patterns and performance optimizations
 * - Clean separation of concerns
 * - NO legacy silent refresh code
 * 
 * CRITICAL: The useEffect patterns are implemented in the HOOKS, not in this component.
 * This component focuses on presentation while hooks handle data management.
 */

import React, { useState, useCallback, useMemo, useEffect, Suspense } from 'react';
import {
  Box, Card, CardContent, Typography, Alert, CircularProgress,
  Tooltip, GlobalStyles, useTheme, Chip, Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Import our refactored hooks - THESE CONTAIN ALL THE useEffect LOGIC
import { useStaffingData } from '../../features/Staffing/hooks/useStaffingData.js';
import { useStaffingCalculations } from '../../features/Staffing/hooks/useStaffingCalculations.js';
import { useStaffingFormData } from '../../features/Staffing/hooks/useStaffingFormData.js';

// Import refactored components
import BudgetSummary from '../../features/Staffing/components/BudgetSummary.jsx';
import MilestoneSection from '../../features/Staffing/components/MilestoneSection.jsx';
import FixedCostsSection from '../../features/Staffing/components/FixedCostsSection.jsx';
import AddRoleDialog from '../../features/Staffing/components/AddRoleDialog.jsx';
import EditRoleDialog from '../../features/Staffing/components/EditRoleDialog.jsx';
import MemberAllocationTable from '../../features/Staffing/components/MemberAllocationTable.jsx';
import TimelineIssuesAlert from '../../features/Staffing/components/TimelineIssuesAlert.jsx';
import StaffingActionButtons from '../../features/Staffing/components/StaffingActionButtons.jsx';
import StaffingHeader from '../../features/Staffing/components/StaffingHeader.jsx';
import CopyRoleMenus from '../../features/Staffing/components/CopyRoleMenus.jsx';

// Import utilities
import {
  roleToFunctionMap,
  roleToFocusMap
} from '../../utils/wizardHelpers';
import * as api from '../../api/api';
import { 
  getRateForRole, 
  getProjectYear, 
  getRoleProjectYear, 
  calculateMemberSalary,
  validateAnnualWorkingDays,
  determineRateType
} from '../../utils/rateUtils';

// Import refactored hooks
import { useRoleCopyOperations } from '../../features/Staffing/hooks/useRoleCopyOperations.js';
import { useTimelineManagement } from '../../features/Staffing/hooks/useTimelineManagement.js';

// Lazy load heavy components
const RoleTimelineModal = React.lazy(() => import('./shared/RoleTimelineModal'));
const TimelineAdjustmentModal = React.lazy(() => import('./shared/TimelineAdjustmentModal'));
const AddMemberModal = React.lazy(() => import('../shared/AddMemberModal'));

// defaultRoles moved to dialog components

// Main StaffingStep Component - FOCUSED ON PRESENTATION
function StaffingStep({
  outcomeId,
  outcomeName,
  data,
  outcomeData,
  onUpdate,
  onFormDataChange,
  buttonStyle,
  secondaryButtonStyle,
  refreshData,
  autoSave = true,
  hideButtons = false,
  readOnly = false
}) {
  const theme = useTheme();
  // Modal states (reduced - many moved to hooks)
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [selectedMilestoneForRole, setSelectedMilestoneForRole] = useState(null);
  const [editingRoleMetadata, setEditingRoleMetadata] = useState(null);
  const [expandedMilestones, setExpandedMilestones] = useState(new Set());
  
  // Department filter state
  const [selectedDeptFilter, setSelectedDeptFilter] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [spendEnrichedOutcomeData, setSpendEnrichedOutcomeData] = useState(null);
  const currentYear = new Date().getFullYear();

  // Load departments on mount
  useEffect(() => {
    api.getDepartments().then(res => {
      const depts = res?.departments || (Array.isArray(res) ? res : []);
      setDepartments(depts);
    }).catch(() => {});
  }, []);

  // Fetch spend data to enrich outcomeData with actuals/forecast fields for EOY card.
  // The wizard passes raw outcomeData from the API which lacks these processed fields.
  useEffect(() => {
    if (!outcomeId) return;
    api.getSpendData(outcomeId, currentYear).then(spendData => {
      if (!spendData) return;
      const monthlyActual = Array(12).fill(null);
      if (spendData.monthlySpend) {
        Object.entries(spendData.monthlySpend).forEach(([mo, amt]) => {
          const idx = parseInt(mo) - 1;
          if (idx >= 0 && idx < 12) monthlyActual[idx] = amt;
        });
      }
      // Find latest month with a non-null, positive actual
      let latestActualSpendMonth = -1;
      for (let i = 11; i >= 0; i--) {
        if (monthlyActual[i] !== null && monthlyActual[i] > 0) { latestActualSpendMonth = i; break; }
      }
      setSpendEnrichedOutcomeData(prev => ({
        ...(prev || outcomeData || {}),
        monthlyActual,
        latestActualSpendMonth,
        officialForecastData: spendData.officialForecast || {},
        companySpend: spendData.companySpend || {},
      }));
    }).catch(() => {});
  }, [outcomeId, currentYear]); // eslint-disable-line react-hooks/exhaustive-deps

  // Form states for dialogs (simplified)
  const [newRoleForm, setNewRoleForm] = useState({
    name: 'Product',
    company: 'CA',
    location: 'US',
    function: 'Product Manager',
    role: '',
    rate: 0
  });

  // Use refactored hooks - ALL useEffect PATTERNS ARE IN THESE HOOKS
  const {
    itRoster,
    rateData,
    locationOptions,
    roleOptions,
    loading,
    basicDataReady,
    enhancementProgress,
    silentRefreshInProgress,
    silentRefreshResult,
    refreshingRates,
    rateUpdateCount,
    metadataUpdateCount,
    error,
    roleUpdateIndicators,
    roleCorruptionIndicators,
    refreshAssignedMemberData,
    clearCacheAndRefresh,
    handleRemoveUpdateIndicator,
    handleRemoveCorruptionIndicator
  } = useStaffingData(outcomeId, outcomeData, data);

  const {
    calculateCapexOpexTotals,
    calculateMilestoneTotals,
    getMemberOverAllocation,
    applyColaToRole,
    approvedBudgetComparison,
    clearCalculationCache
  } = useStaffingCalculations(data, outcomeData, itRoster, rateData);

  const {
    formData,
    saving,
    saveError,
    updateFormData: _updateFormData,
    setIsUpdating,
    handleSave,
    migrateRoleToCalendarFormat,
    applyUpdatedRoles
  } = useStaffingFormData(
    data,
    outcomeId,
    outcomeName,
    outcomeData,
    onUpdate,
    autoSave,
    readOnly
  );

  const updateFormData = _updateFormData;

  // Use timeline management hook
  const {
    timelineModalOpen,
    setTimelineModalOpen,
    pendingTimelineChange,
    setPendingTimelineChange,
    editingRole,
    setEditingRole,
    roleTimelineModalOpen,
    setRoleTimelineModalOpen,
    rolesWithoutTimeline,
    setRolesWithoutTimeline,
    handleTimelineAdjustmentConfirm,
    handleEditMilestoneRoleTimeline,
    handleRoleTimelineUpdate
  } = useTimelineManagement({
    formData,
    outcomeData,
    updateFormData,
    setIsUpdating,
    migrateRoleToCalendarFormat
  });

  // Use role copy operations hook
  const {
    copyMenuAnchor,
    roleToCopy,
    copyAllRolesMenuAnchor,
    sourceMilestoneForCopy,
    handleCopyRoleClick,
    handleCopyRoleClose,
    handleCopyRoleToMilestone,
    handleCopyAllRolesClick,
    handleCopyAllRolesClose,
    handleCopyAllRolesToMilestone
  } = useRoleCopyOperations({
    updateFormData,
    setIsUpdating,
    readOnly,
    saving
  });

  // Merge refreshed rates into formData whenever a silent refresh completes.
  // Covers both the automatic (mount-time) and manual refresh paths.
  useEffect(() => {
    if (silentRefreshResult?.updated && silentRefreshResult.updatedRoles?.length > 0) {
      applyUpdatedRoles(silentRefreshResult.updatedRoles);
    }
  }, [silentRefreshResult, applyUpdatedRoles]);

  // CRITICAL: Only ONE useEffect in main component - for expanded milestones
  // This must come AFTER the hooks that provide formData
  useEffect(() => {
    if (formData?.milestones && formData.milestones.length > 0) {
      setExpandedMilestones(new Set(formData.milestones.map(m => m.id)));
    }
  }, [formData?.milestones]);

  // Keep parent ref in sync with actual formData state (avoids stale closure in tab-switch save)
  useEffect(() => {
    if (onFormDataChange) onFormDataChange(formData);
  }, [formData, onFormDataChange]);

  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  // Merge spend-fetched fields with outcomeData; prefer spendEnrichedOutcomeData for actuals/forecast.
  // Also compute monthlyProjected from the live formData so the EOY card works without the FVA pipeline.
  const effectiveOutcomeData = useMemo(() => {
    const base = spendEnrichedOutcomeData ? { ...outcomeData, ...spendEnrichedOutcomeData } : outcomeData;
    // Build per-company monthly breakdown + total monthly projected from formData
    const companyBreakdown = {};
    const fixedCostBreakdown = {};
    (formData?.milestones || []).forEach(milestone => {
      (milestone.roles || []).forEach(role => {
        const co = role.company || 'CA';
        if (!companyBreakdown[co]) companyBreakdown[co] = { monthlyForecast: Array(12).fill(0), monthlyCapex: Array(12).fill(0), monthlyOpex: Array(12).fill(0), total: 0, capex: 0, opex: 0 };
        const rate = role.rate || 0;
        const isCapex = role.capex_classification === 'CAPEX';
        (role.dateRanges || []).forEach(dr => {
          const start = Math.max(1, Math.min(12, dr.startMonth || 1));
          const end = Math.max(start, Math.min(12, dr.endMonth || start));
          const alloc = Math.max(0, Math.min(100, dr.allocation != null ? dr.allocation : (role.allocation || 0))) / 100;
          for (let m = start; m <= end; m++) {
            const amt = rate * alloc;
            companyBreakdown[co].monthlyForecast[m - 1] += amt;
            if (isCapex) companyBreakdown[co].monthlyCapex[m - 1] += amt;
            else         companyBreakdown[co].monthlyOpex[m - 1]  += amt;
          }
        });
      });
    });
    Object.values(companyBreakdown).forEach(c => {
      c.total = c.monthlyForecast.reduce((s, v) => s + v, 0);
      c.capex  = c.monthlyCapex.reduce((s, v) => s + v, 0);
      c.opex   = c.monthlyOpex.reduce((s, v) => s + v, 0);
    });
    (formData?.fixedCosts || []).forEach((cost, idx) => {
      const name = cost.type || cost.name || `Fixed Cost ${idx + 1}`;
      const co = cost.company || 'Other';
      if (!fixedCostBreakdown[name]) fixedCostBreakdown[name] = { monthlyForecast: Array(12).fill(0), total: 0, company: co };
      if (!companyBreakdown[co]) companyBreakdown[co] = { monthlyForecast: Array(12).fill(0), monthlyCapex: Array(12).fill(0), monthlyOpex: Array(12).fill(0), total: 0, capex: 0, opex: 0 };
      try {
        const fcStart = Math.max(1, Math.min(12, new Date(cost.start_date + '-01').getMonth() + 1));
        const fcEnd = Math.max(fcStart, Math.min(12, new Date(cost.end_date + '-01').getMonth() + 1));
        const mo = cost.monthlyCost || cost.monthly_cost || 0;
        const isCapex = (cost.capex_classification || 'OPEX') === 'CAPEX';
        for (let m = fcStart; m <= fcEnd; m++) {
          companyBreakdown[co].monthlyForecast[m - 1] += mo;
          if (isCapex) companyBreakdown[co].monthlyCapex[m - 1] += mo;
          else         companyBreakdown[co].monthlyOpex[m - 1]  += mo;
          fixedCostBreakdown[name].monthlyForecast[m - 1] += mo;
        }
        companyBreakdown[co].total = companyBreakdown[co].monthlyForecast.reduce((acc, v) => acc + v, 0);
        companyBreakdown[co].capex = companyBreakdown[co].monthlyCapex.reduce((acc, v) => acc + v, 0);
        companyBreakdown[co].opex  = companyBreakdown[co].monthlyOpex.reduce((acc, v) => acc + v, 0);
        fixedCostBreakdown[name].total = fixedCostBreakdown[name].monthlyForecast.reduce((acc, v) => acc + v, 0);
      } catch (_) {}
    });
    const monthlyProjected = Array(12).fill(0);
    const monthlyCapex = Array(12).fill(0);
    const monthlyOpex  = Array(12).fill(0);
    Object.values(companyBreakdown).forEach(c => {
      c.monthlyForecast.forEach((v, i) => { monthlyProjected[i] += v; });
      c.monthlyCapex.forEach((v, i) => { monthlyCapex[i] += v; });
      c.monthlyOpex.forEach((v, i)  => { monthlyOpex[i]  += v; });
    });
    return { ...base, monthlyProjected, monthlyCapex, monthlyOpex, companyBreakdown, fixedCostBreakdown };
  }, [outcomeData, spendEnrichedOutcomeData, formData?.milestones, formData?.fixedCosts]); // eslint-disable-line react-hooks/exhaustive-deps

  const eoyBreakdown = useMemo(() => {
    const cutoff = effectiveOutcomeData?.latestActualSpendMonth ?? -1;
    const monthlyActual = effectiveOutcomeData?.monthlyActual || [];
    const monthlyProjected = effectiveOutcomeData?.monthlyProjected || [];
    if (cutoff < 0 || monthlyProjected.length === 0) return null;

    const ytdActuals = [];
    for (let i = 0; i <= cutoff; i++) {
      ytdActuals.push({ month: MONTH_NAMES[i], amount: monthlyActual[i] || 0 });
    }

    const hasOfficial = !!(effectiveOutcomeData?.officialForecastData?.companyMonthlyForecast?.['CA']);
    const caMonthlySource = effectiveOutcomeData?.officialForecastData?.companyMonthlyForecast?.['CA']
      || effectiveOutcomeData?.officialForecastData?.monthlyForecast
      || {};
    const caForecastRemaining = [];
    for (let i = cutoff + 1; i < 12; i++) {
      const mo = String(i + 1);
      const amount = hasOfficial
        ? (caMonthlySource[mo] || 0)
        : (effectiveOutcomeData?.companyBreakdown?.['CA']?.monthlyForecast?.[i] || 0);
      caForecastRemaining.push({ month: MONTH_NAMES[i], amount, source: hasOfficial ? 'official' : 'staffing' });
    }

    const companyBreakdown = effectiveOutcomeData?.companyBreakdown || {};
    const fixedCostBreakdown = effectiveOutcomeData?.fixedCostBreakdown || {};
    const remainingMonths = Math.max(1, 11 - cutoff);
    const nonCaCompanies = Object.entries(companyBreakdown)
      .filter(([co]) => co !== 'CA')
      .map(([name, data]) => ({
        name,
        monthlyAmount: data.monthlyForecast
          ? data.monthlyForecast.slice(cutoff + 1).reduce((s, v) => s + (v || 0), 0) / remainingMonths
          : 0
      }))
      .filter(c => c.monthlyAmount > 0);
    const fixedCosts = Object.entries(fixedCostBreakdown)
      .map(([name, data]) => ({
        name,
        monthlyAmount: data.monthlyForecast
          ? data.monthlyForecast.slice(cutoff + 1).reduce((s, v) => s + (v || 0), 0) / remainingMonths
          : 0
      }))
      .filter(f => f.monthlyAmount > 0);
    const totalPerMonth = [...nonCaCompanies, ...fixedCosts].reduce((s, x) => s + x.monthlyAmount, 0);

    return { cutoff, cutoffLabel: MONTH_NAMES[cutoff], ytdActuals, caForecastRemaining, hasOfficial, ppmRemaining: { companies: nonCaCompanies, fixedCosts, totalPerMonth } };
  }, [effectiveOutcomeData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actual CAPEX/OPEX — from uploaded spend data (companySpend is stored in spendEnrichedOutcomeData)
  const actualCapex = useMemo(() => {
    const companySpend = spendEnrichedOutcomeData?.companySpend || {};
    return Object.values(companySpend).reduce((s, c) => s + (c.capex || 0), 0);
  }, [spendEnrichedOutcomeData]);

  const actualOpex = useMemo(() => {
    const companySpend = spendEnrichedOutcomeData?.companySpend || {};
    return Object.values(companySpend).reduce((s, c) => s + (c.opex || 0), 0);
  }, [spendEnrichedOutcomeData]);

  // EOY CAPEX/OPEX blend: actual C/O through cutoff + projected C/O for remainder
  const { eoyCapex, eoyOpex } = useMemo(() => {
    const cutoff = effectiveOutcomeData?.latestActualSpendMonth ?? -1;
    if (cutoff < 0) return { eoyCapex: 0, eoyOpex: 0 };
    const mc = effectiveOutcomeData?.monthlyCapex || [];
    const mo = effectiveOutcomeData?.monthlyOpex  || [];
    return {
      eoyCapex: actualCapex + mc.slice(cutoff + 1).reduce((s, v) => s + (v || 0), 0),
      eoyOpex:  actualOpex  + mo.slice(cutoff + 1).reduce((s, v) => s + (v || 0), 0),
    };
  }, [effectiveOutcomeData, actualCapex, actualOpex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Spend status — mirrors FVA step's calculateSpendStatus logic
  const spendStatus = useMemo(() => {
    const cutoff = effectiveOutcomeData?.latestActualSpendMonth ?? -1;
    if (cutoff < 0) return null;
    const monthlyActual = effectiveOutcomeData?.monthlyActual || [];
    const monthlyProjected = effectiveOutcomeData?.monthlyProjected || [];
    const approvedBudget = approvedBudgetComparison?.totalApproved || 0;

    const ytdActual = monthlyActual.slice(0, cutoff + 1).reduce((s, v) => s + (v || 0), 0);
    const ytdProjected = monthlyProjected.slice(0, cutoff + 1).reduce((s, v) => s + (v || 0), 0);
    const totalProjected = monthlyProjected.reduce((s, v) => s + (v || 0), 0);

    const projectedOverBudget = approvedBudget > 0 && totalProjected > approvedBudget;
    const ytdOverProjected = ytdActual > 0 && ytdProjected > 0 && ytdActual > ytdProjected;

    if (projectedOverBudget && ytdOverProjected) return { status: 'needs-attention', text: 'Needs Attention' };
    if (!projectedOverBudget && ytdOverProjected) return { status: 'at-risk', text: 'At Risk' };
    if (projectedOverBudget)                      return { status: 'off-track', text: 'Off Track' };
    return { status: 'on-track', text: 'On Track' };
  }, [effectiveOutcomeData, approvedBudgetComparison]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpdateMilestoneDepts = useCallback((milestoneId, newDepts) => {
    const updated = (formData?.milestones || []).map(m =>
      m.id === milestoneId ? { ...m, departments: newDepts } : m
    );
    updateFormData({ milestones: updated });
  }, [formData?.milestones, updateFormData]);

  console.log('🔍 StaffingStep (Refactored): Rendering with data:', {
    formDataMilestones: formData?.milestones?.length || 0,
    itRosterLength: itRoster.length,
    basicDataReady,
    loading,
    enhancementProgress
  });

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }, []);

  // Render rate with indicators
  const renderRateWithIndicator = useCallback((role) => {
    const hasAdjustment = role.rateAdjustmentInfo;
    const calculatedYear = role.calculatedForYear || getProjectYear(outcomeData, formData);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          ${Math.round(role.rate || 0).toLocaleString()}/mo • {role.company} • {role.function}
          {calculatedYear && ` • ${calculatedYear}`}
          {role.rateType && ` • ${role.rateType}`}
        </Typography>
        
        {hasAdjustment && (
          <Tooltip
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Timeline-Based COLA Rate
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Base: ${Math.round(hasAdjustment.baseRate).toLocaleString()} ({hasAdjustment.baseYear})
                </Typography>
                <Typography variant="body2">
                  Adjusted: ${Math.round(role.rate).toLocaleString()} ({calculatedYear})
                </Typography>
                <Typography variant="body2">
                  COLA Rate: {hasAdjustment.colaRate}% annually
                </Typography>
              </Box>
            }
            arrow
          >
            <CalculateIcon sx={{ fontSize: '0.75rem', color: 'warning.main', cursor: 'help' }} />
          </Tooltip>
        )}
        
        {roleCorruptionIndicators.has(role.id) && (
          <Tooltip title="Corrupted Rate Data Detected" arrow>
            <Box
              sx={{
                fontSize: '0.9rem',
                color: 'error.main',
                cursor: 'pointer',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }}
                              onClick={(e) => {
                  e && e.stopPropagation && e.stopPropagation();
                  handleRemoveCorruptionIndicator(role.id);
                  if (refreshData) {
                  refreshData();
                }
              }}
            >
              🐛
            </Box>
          </Tooltip>
        )}
        
        {roleUpdateIndicators.has(role.id) && (
          <Tooltip title="Role Updated" arrow>
            <CheckCircleIcon 
              sx={{ 
                fontSize: '0.75rem', 
                color: 'success.main',
                cursor: 'pointer',
                ml: 0.5
              }}
                              onClick={(e) => {
                  e && e.stopPropagation && e.stopPropagation();
                  handleRemoveUpdateIndicator(role.id);
                }}
            />
          </Tooltip>
        )}
      </Box>
    );
  }, [roleCorruptionIndicators, roleUpdateIndicators, handleRemoveCorruptionIndicator, handleRemoveUpdateIndicator, outcomeData, formData, refreshData]);

  // Event handlers - SIMPLIFIED (complex logic moved to hooks)
  const handleRefresh = useCallback(async () => {
    console.log('🔄 Manual refresh triggered');
    clearCalculationCache();
    
    if (formData?.milestones && formData.milestones.length > 0) {
      const allRoles = formData.milestones.flatMap(milestone => milestone.roles || []);
      await refreshAssignedMemberData(allRoles);
    }
    
    if (refreshData) {
      await refreshData();
    }
  }, [clearCalculationCache, formData?.milestones, refreshAssignedMemberData, refreshData]);

  const handleApplyCola = useCallback(() => {
    if (formData?.milestones) {
      const allRoles = formData.milestones.flatMap(m => m.roles || []);
      allRoles.forEach(role => {
        if (applyColaToRole) {
          applyColaToRole(role);
        }
      });
    }
  }, [formData?.milestones, applyColaToRole]);

  const handleOpenAddRoleDialog = useCallback(async (milestone) => {
    setSelectedMilestoneForRole(milestone);

    const projectYear = getProjectYear(outcomeData, null, new Date().getFullYear());
    try {
      const rateInfo = await getRateForRole('CA', 'US', '', projectYear);
      setNewRoleForm({
        name: 'Product',
        company: 'CA',
        location: 'US',
        function: 'Product Manager',
        role: '',
        rate: rateInfo.monthlyRate || 13583,
        rateAdjustmentInfo: rateInfo.isAdjusted ? rateInfo.adjustmentInfo : null
      });
    } catch (error) {
      console.warn('Could not get COLA-adjusted rate for new role:', error);
      setNewRoleForm({
        name: 'Product',
        company: 'CA',
        location: 'US',
        function: 'Product Manager',
        role: '',
        rate: 13583,
        rateAdjustmentInfo: null
      });
    }

    setAddRoleDialogOpen(true);
  }, [outcomeData]);

  const handleOpenEditRoleDialog = useCallback((milestone, role) => {
    setEditingRoleMetadata({ milestone, role });
    setEditRoleDialogOpen(true);
  }, []);

  const handleMilestoneRoleChange = useCallback((milestoneId, roleId, field, value) => {
    if (readOnly || saving) return;

    setIsUpdating(true);
    
    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === milestoneId) {
          return {
            ...milestone,
            roles: milestone.roles.map(role => {
              if (role.id === roleId) {
                let updated = { ...role, [field]: value };

                // Handle allocation changes
                if (field === 'allocation') {
                  const allocationValue = Math.max(0, Math.min(100, Number(value) || 0));
                  updated.allocation = allocationValue;
                  
                  if (updated.dateRanges && updated.dateRanges.length > 0) {
                    updated.dateRanges = updated.dateRanges.map(range => ({
                      ...range,
                      allocation: allocationValue
                    }));
                  }
                }

                // Handle member assignment
                if (field === 'roster_member_id' && value) {
                  const member = itRoster.find(m => m.id === value);
                  if (member) {
                    updated = {
                      ...updated,
                      company: member.company || updated.company || "CA",
                      location: member.location || updated.location || "US",
                      function: member.function || updated.function || roleToFunctionMap[updated.name] || "Other",
                      focus: member.function === "Developer" ? (member.focus || roleToFocusMap[updated.name] || "Fullstack") : undefined,
                      role: member.role || updated.role || updated.name,
                      department: member.department || updated.department,
                      lastMetadataUpdate: new Date().toISOString(),
                      metadataSource: 'assignee',
                      annualWorkingDays: validateAnnualWorkingDays(member.company === 'CA' ? 252 : 260, `member ${member.name}`),
                      effectiveAnnualWorkingDays: validateAnnualWorkingDays(member.company === 'CA' ? 252 : 260, `member ${member.name}`),
                      rateType: determineRateType(member.company === 'CA' ? 252 : 260)
                    };

                    // Apply COLA rate asynchronously
                    const roleProjectYear = getRoleProjectYear(role, milestone, outcomeData, formData, new Date().getFullYear());
                    calculateMemberSalary(member, roleProjectYear)
                      .then(memberSalaryInfo => {
                        updateFormData(current => ({
                          ...current,
                          milestones: current.milestones.map(m => {
                            if (m.id === milestoneId) {
                              return {
                                ...m,
                                roles: m.roles.map(r => {
                                  if (r.id === roleId) {
                                    return {
                                      ...r,
                                      rate: memberSalaryInfo.monthlySalary || 0,
                                      rateAdjustmentInfo: memberSalaryInfo.isAdjusted ? memberSalaryInfo.adjustmentInfo : null,
                                      calculatedForYear: roleProjectYear
                                    };
                                  }
                                  return r;
                                })
                              };
                            }
                            return m;
                          })
                        }));
                      })
                      .catch(error => {
                        console.warn('Could not calculate member salary:', error);
                      });
                  }
                }

                return updated;
              }
              return role;
            })
          };
        }
        return milestone;
      })
    }));

    setTimeout(() => setIsUpdating(false), 500);
  }, [readOnly, saving, updateFormData, itRoster, outcomeData, formData, setIsUpdating]);

  const handleAddRoleToMilestone = useCallback(() => {
    if (readOnly || saving || !selectedMilestoneForRole) return;

    setIsUpdating(true);
    const milestone = selectedMilestoneForRole;
    const mappedFocus = newRoleForm.function === "Developer" ? (roleToFocusMap[newRoleForm.name] || "Fullstack") : undefined;

    const milestoneStart = dayjs(milestone.start_date);
    const milestoneEnd = dayjs(milestone.end_date);

    // NEW FORMAT: Use direct calendar months (1-12)
    const milestoneStartCalendarMonth = milestoneStart.month() + 1;
    const milestoneEndCalendarMonth = milestoneEnd.month() + 1;
    const milestoneDuration = Math.max(1, milestoneEnd.diff(milestoneStart, 'month') + 1);

    const defaultWorkingDays = newRoleForm.company === 'CA' ? 252 : 260;
    const roleWorkingDays = validateAnnualWorkingDays(defaultWorkingDays, `new role ${newRoleForm.name}`);

    const newRole = {
      id: `${milestone.id}-${newRoleForm.name}-${Date.now()}`,
      milestone_id: milestone.id,
      name: newRoleForm.name,
      company: newRoleForm.company,
      location: newRoleForm.location,
      function: newRoleForm.function,
      focus: mappedFocus,
      role: newRoleForm.role,
      rate: newRoleForm.rate,
      roster_member_id: "",
      notes: "",
      allocation: 100,
      capex_classification: milestone.capex_classification || 'OPEX',
      capex_source: 'milestone',
      original_role_id: `${newRoleForm.name}-${Date.now()}`,
      milestone_start_month: milestoneStartCalendarMonth,
      milestone_end_month: milestoneEndCalendarMonth,
      overlap_months: milestoneDuration,
      useDefaultTimeline: true,
      defaultAllocation: 100,
      dateRanges: [{
        startMonth: milestoneStartCalendarMonth,
        endMonth: milestoneEndCalendarMonth,
        allocation: 100,
        timelineVersion: '3.0'
      }],
      rateAdjustmentInfo: newRoleForm.rateAdjustmentInfo,
      calculatedForYear: newRoleForm.calculatedForYear,
      lastRateUpdate: new Date().toISOString(),
      timelineVersion: '2.0',
      annualWorkingDays: roleWorkingDays,
      effectiveAnnualWorkingDays: roleWorkingDays,
      rateType: determineRateType(roleWorkingDays)
    };

    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(m => {
        if (m.id === milestone.id) {
          return {
            ...m,
            roles: [...(m.roles || []), newRole]
          };
        }
        return m;
      })
    }));

    setTimeout(() => setIsUpdating(false), 500);
    setAddRoleDialogOpen(false);
    setSelectedMilestoneForRole(null);
  }, [readOnly, saving, selectedMilestoneForRole, newRoleForm, updateFormData, setIsUpdating]);

  const handleRemoveRoleFromMilestone = useCallback((milestoneId, roleId) => {
    if (readOnly || saving) return;

    setIsUpdating(true);
    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === milestoneId) {
          return {
            ...milestone,
            roles: (milestone.roles || []).filter(role => role.id !== roleId)
          };
        }
        return milestone;
      })
    }));
    setTimeout(() => setIsUpdating(false), 500);
  }, [readOnly, saving, updateFormData, setIsUpdating]);

  const handleMoveRole = useCallback((milestoneId, roleId, direction) => {
    if (readOnly || saving) return;

    setIsUpdating(true);
    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === milestoneId) {
          const roles = milestone.roles || [];
          const currentIndex = roles.findIndex(role => role.id === roleId);
          if (currentIndex === -1) return milestone;

          const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
          if (newIndex >= 0 && newIndex < roles.length) {
            const newRoles = [...roles];
            [newRoles[currentIndex], newRoles[newIndex]] = [newRoles[newIndex], newRoles[currentIndex]];
            return {
              ...milestone,
              roles: newRoles
            };
          }
        }
        return milestone;
      })
    }));
    setTimeout(() => setIsUpdating(false), 500);
  }, [readOnly, saving, updateFormData, setIsUpdating]);

  // handleEditMilestoneRoleTimeline is provided by useTimelineManagement hook

  // handleRoleTimelineUpdate is provided by useTimelineManagement hook

  const toggleMilestoneExpansion = useCallback((milestoneId) => {
    setExpandedMilestones(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(milestoneId)) {
        newExpanded.delete(milestoneId);
      } else {
        newExpanded.add(milestoneId);
      }
      return newExpanded;
    });
  }, []);

  // New role form changes - SIMPLIFIED (complex logic moved to dialog component)
  const handleNewRoleFormChange = useCallback(async (field, value) => {
    setNewRoleForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // BACKWARDS COMPATIBILITY: Validation moved to RoleCopyService
  const validateBackwardsCompatibility = useCallback((data) => {
    // Simplified validation - full logic moved to service layer
    return { isValid: true, issues: [], fixes: [] };
  }, []);

  // Copy role handlers moved to useRoleCopyOperations hook

  // Copy role to milestone logic moved to useRoleCopyOperations hook

  // Copy all roles handlers moved to useRoleCopyOperations hook

  // Copy all roles to milestone logic moved to useRoleCopyOperations hook

  // Calculate member allocation summary - SIMPLIFIED (complex logic moved to service)
  const memberAllocationSummary = useMemo(() => {
    if (!formData?.milestones) {
      return { assignedMembers: [], unassignedRoles: [] };
    }

    const memberSummary = new Map();
    const unassignedSummary = [];

    // Simplified calculation - full logic can be moved to a service later
    formData.milestones.forEach(milestone => {
      // Skip milestones hidden by dept tag filter (same logic as milestone render loop)
      if (selectedDeptFilter && milestone.departments && milestone.departments.length > 0) {
        if (!milestone.departments.includes(selectedDeptFilter)) return;
      }
      milestone.roles?.forEach(role => {
        // When a dept filter is active, only include matching roles
        if (selectedDeptFilter && role.department && role.department !== selectedDeptFilter) return;
        const monthlyAllocations = Array(12).fill(0);
        const allocation = role.allocation || 0;
        const months = role.overlap_months || 1;
        const monthlyRate = role.rate || 0;
        const totalCost = monthlyRate * (allocation / 100) * months;

        // Populate monthly allocations respecting actual calendar months
        if (role.dateRanges && role.dateRanges.length > 0) {
          // dateRanges use calendar months: startMonth/endMonth are 1-12
          role.dateRanges.forEach(dr => {
            const rangeAllocation = dr.allocation != null ? dr.allocation : allocation;
            const startMonth = dr.startMonth;
            const endMonth = dr.endMonth;
            if (startMonth == null || endMonth == null) return;
            for (let month = startMonth; month <= endMonth; month++) {
              const monthIndex = month - 1; // Convert 1-based calendar month to 0-based index
              if (monthIndex >= 0 && monthIndex < 12) {
                monthlyAllocations[monthIndex] = rangeAllocation;
              }
            }
          });
        } else {
          // Fallback: sequential fill from index 0 when no dateRanges are present
          for (let i = 0; i < Math.min(months, 12); i++) {
            monthlyAllocations[i] = allocation;
          }
        }

        if (role.roster_member_id) {
          // Assigned member
          if (!memberSummary.has(role.roster_member_id)) {
            const member = itRoster.find(m => m.id === role.roster_member_id);
            memberSummary.set(role.roster_member_id, {
              id: role.roster_member_id,
              name: member?.name || 'Unknown Member',
              monthlyAllocations: Array(12).fill(0),
              totalCost: 0
            });
          }
          const memberData = memberSummary.get(role.roster_member_id);
          monthlyAllocations.forEach((allocation, index) => {
            memberData.monthlyAllocations[index] += allocation;
          });
          memberData.totalCost += totalCost;
        } else {
          // Unassigned role
          unassignedSummary.push({
            id: `unassigned-${role.id}`,
            name: `${role.name || role.function || 'Unknown Role'} (Unassigned)`,
            monthlyAllocations,
            totalCost,
            isUnassigned: true
          });
        }
      });
    });

    return {
      assignedMembers: Array.from(memberSummary.values()),
      unassignedRoles: unassignedSummary
    };
  }, [formData?.milestones, itRoster, selectedDeptFilter]);

  // Helper functions for allocation status - SIMPLIFIED
  const getAllocationStatus = useCallback((monthlyAllocations) => {
    const maxAllocation = Math.max(...monthlyAllocations);
    if (maxAllocation > 100) return 'Over Allocated';
    if (monthlyAllocations.some(allocation => allocation < 100 && allocation > 0)) return 'Under Allocated';
    if (monthlyAllocations.every(allocation => allocation === 0)) return 'Under Allocated';
    return 'Fully Allocated';
  }, []);

  const getAllocationColor = useCallback((percentage) => {
    if (percentage > 100) return 'error';
    if (percentage === 100) return 'success';
    if (percentage > 0) return 'info';
    return 'default';
  }, []);

  // Timeline adjustment confirmation moved to useTimelineManagement hook

  // Button styles
  const localButtonStyle = buttonStyle || {
    backgroundColor: theme.palette.mode === 'dark' ? '#4db6ac' : undefined,
    color: theme.palette.mode === 'dark' ? theme.palette.common.black : undefined,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#00897b' : undefined,
    }
  };

  // Simple loading state
  if (loading && !basicDataReady) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <CircularProgress size={20} />
          <Typography variant="h6">Loading staffing data...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Global styles for corruption indicator animation */}
      <GlobalStyles
        styles={{
          '@keyframes pulse': {
            '0%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.5,
            },
            '100%': {
              opacity: 1,
            },
          },
        }}
      />
      <Box sx={{ p: 3 }}>
              {/* Header Section - EXTRACTED */}
      <StaffingHeader
        formData={formData}
        saving={saving}
        saveError={saveError}
        refreshingRates={refreshingRates}
        rateUpdateCount={rateUpdateCount}
        metadataUpdateCount={metadataUpdateCount}
        itRoster={itRoster}
        basicDataReady={basicDataReady}
        enhancementProgress={enhancementProgress}
        onRetrySave={handleSave}
      />

      {/* Timeline Issues Alert - EXTRACTED */}
      <TimelineIssuesAlert
        rolesWithoutTimeline={rolesWithoutTimeline}
        formData={formData}
        onFixTimeline={handleMilestoneRoleChange}
        onDismiss={() => setRolesWithoutTimeline([])}
        readOnly={readOnly}
      />

      {/* Main Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight={600}>
            Staffing Plan
          </Typography>
          
          {/* Enhanced Loading Indicators */}
          {(loading || silentRefreshInProgress || enhancementProgress.silentRefresh === 'loading' || refreshingRates) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={20} />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" color="primary.main" fontWeight={500}>
                  {loading ? 'Loading data...' :
                   refreshingRates ? 'Refreshing rates...' :
                   silentRefreshInProgress ? 'Updating rates...' :
                   enhancementProgress.silentRefresh === 'loading' ? 'Processing updates...' : 'Processing...'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {loading ? 'Fetching roster and rates' :
                   refreshingRates ? `Updated ${rateUpdateCount} rates, ${metadataUpdateCount} metadata` :
                   silentRefreshInProgress ? 'Applying COLA adjustments' :
                   enhancementProgress.silentRefresh === 'loading' ? 'Almost ready...' : 'Processing...'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Action Buttons - EXTRACTED */}
      <StaffingActionButtons
        onRefreshData={refreshData}
        onRefreshMemberData={handleRefresh}
        onApplyCola={handleApplyCola}
        onAddMember={() => setAddMemberModalOpen(true)}
        onSave={handleSave}
        loading={loading}
        saving={saving}
        refreshingRates={refreshingRates}
        readOnly={readOnly}
        hideButtons={hideButtons}
        localButtonStyle={localButtonStyle}
        secondaryButtonStyle={secondaryButtonStyle}
      />

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Save Error */}
      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to save changes. Please try again.
        </Alert>
      )}

      {/* Data Status - moved to StaffingHeader component */}

      {/* Budget Summary */}
      <BudgetSummary
        formData={formData}
        outcomeData={effectiveOutcomeData}
        calculateCapexOpexTotals={calculateCapexOpexTotals}
        approvedBudgetComparison={approvedBudgetComparison}
        formatCurrency={formatCurrency}
        eoyTotal={(() => {
          const cutoff = effectiveOutcomeData?.latestActualSpendMonth ?? -1;
          if (cutoff < 0) return 0;
          const monthlyActual = effectiveOutcomeData?.monthlyActual || [];
          const monthlyProjected = effectiveOutcomeData?.monthlyProjected || [];
          if (monthlyProjected.length === 0) return 0;
          const officialByCompany = effectiveOutcomeData?.officialForecastData?.companyMonthlyForecast || {};
          const hasOfficial = Object.keys(officialByCompany).length > 0;
          let total = 0;
          for (let i = 0; i < 12; i++) {
            if (i <= cutoff) {
              total += monthlyActual[i] || 0;
            } else if (hasOfficial) {
              const mo = String(i + 1);
              total += Object.values(officialByCompany).reduce((s, cm) => s + (cm[mo] || 0), 0);
            } else {
              total += monthlyProjected[i] || 0;
            }
          }
          return total;
        })()}
        ppmEoyTotal={(() => {
          const cutoff = effectiveOutcomeData?.latestActualSpendMonth ?? -1;
          if (cutoff < 0) return 0;
          const monthlyActual = effectiveOutcomeData?.monthlyActual || [];
          const monthlyProjected = effectiveOutcomeData?.monthlyProjected || [];
          if (monthlyProjected.length === 0) return 0;
          return monthlyProjected.reduce((s, proj, i) =>
            s + (i <= cutoff ? (monthlyActual[i] || 0) : proj), 0);
        })()}
        eoyBreakdown={eoyBreakdown}
        eoyCapex={eoyCapex}
        eoyOpex={eoyOpex}
        spendStatus={spendStatus}
      />

      {/* Department filter chips — restricted to this outcome's departments */}
      {(() => {
        const outcomeDeptNames = (outcomeData?.department_budgets || []).map(db => db.department_name);
        const chipDepts = outcomeDeptNames.length > 0
          ? departments.filter(d => outcomeDeptNames.includes(d.name))
          : departments;
        if (chipDepts.length === 0) return null;
        return (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Filter by dept:</Typography>
            <Chip
              label="All"
              size="small"
              onClick={() => setSelectedDeptFilter(null)}
              color={!selectedDeptFilter ? 'primary' : 'default'}
              variant={!selectedDeptFilter ? 'filled' : 'outlined'}
            />
            {chipDepts.map(d => (
              <Chip
                key={d.id}
                label={d.name}
                size="small"
                onClick={() => setSelectedDeptFilter(selectedDeptFilter === d.name ? null : d.name)}
                color={selectedDeptFilter === d.name ? 'warning' : 'default'}
                variant={selectedDeptFilter === d.name ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        );
      })()}

      {/* Dept budget breakdown card */}
      {selectedDeptFilter && (() => {
        const deptBudget = (outcomeData?.department_budgets || []).find(db => db.department_name === selectedDeptFilter);
        const deptApproved = deptBudget ? (deptBudget.capex + deptBudget.opex) : 0;
        const calcRoleCost = (r) => {
          const months = (r.dateRanges || []).reduce((s, dr) =>
            s + Math.max(0, (dr.endMonth || 1) - (dr.startMonth || 1) + 1), 0
          ) || Math.max(1, r.overlap_months || 1);
          return (r.rate || 0) * ((r.allocation || 100) / 100) * months;
        };
        const allRoles = (formData?.milestones || []).flatMap(m => m.roles || []);
        const deptStaffed = allRoles.filter(r => r.department === selectedDeptFilter).reduce((s, r) => s + calcRoleCost(r), 0);
        const totalStaffed = allRoles.reduce((s, r) => s + calcRoleCost(r), 0);
        const totalApproved = (outcomeData?.approved_budget?.capex || 0) + (outcomeData?.approved_budget?.opex || 0);
        const remaining = deptApproved - deptStaffed;
        const pctOfTotal = totalStaffed > 0 ? (deptStaffed / totalStaffed) * 100 : 0;
        const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
        return (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" gutterBottom>
                {selectedDeptFilter} — Department Budget
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Staffed</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmt(deptStaffed)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Dept Budget</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {deptApproved > 0 ? fmt(deptApproved) : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Remaining</Typography>
                  <Typography variant="body2" fontWeight={600}
                    color={remaining < 0 ? 'error.main' : 'success.main'}>
                    {deptApproved > 0 ? fmt(remaining) : '—'}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="caption" color="text.secondary">% of Total Staffed</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {pctOfTotal.toFixed(1)}% of {fmt(totalStaffed)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Dept Budget vs Total Approved</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {deptApproved > 0 ? fmt(deptApproved) : '—'} / {fmt(totalApproved)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })()}

      {/* Milestones */}
      {formData?.milestones && formData.milestones.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Milestones ({formData.milestones.length})
            </Typography>

            {formData.milestones.filter(m => {
              if (!selectedDeptFilter) return true;
              if (!m.departments || m.departments.length === 0) return true;
              return m.departments.includes(selectedDeptFilter);
            }).map((milestone) => (
              <MilestoneSection
                key={milestone.id}
                milestone={milestone}
                expanded={expandedMilestones.has(milestone.id)}
                onToggleExpansion={() => toggleMilestoneExpansion(milestone.id)}
                onRoleChange={handleMilestoneRoleChange}
                onAddRole={() => handleOpenAddRoleDialog(milestone)}
                onEditRole={(role) => handleOpenEditRoleDialog(milestone, role)}
                onEditTimeline={(role) => handleEditMilestoneRoleTimeline(milestone, role)}
                onRemoveRole={handleRemoveRoleFromMilestone}
                onMoveRole={handleMoveRole}
                onCopyRole={handleCopyRoleClick}
                onCopyAllRoles={handleCopyAllRolesClick}
                calculateMilestoneTotals={calculateMilestoneTotals}
                getMemberOverAllocation={getMemberOverAllocation}
                renderRateWithIndicator={renderRateWithIndicator}
                formatCurrency={formatCurrency}
                itRoster={itRoster}
                locationOptions={locationOptions}
                roleOptions={roleOptions}
                readOnly={readOnly}
                saving={saving}
                deptFilter={selectedDeptFilter}
                departments={departments}
                onUpdateMilestoneDepts={handleUpdateMilestoneDepts}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Fixed Costs */}
      <FixedCostsSection
        fixedCosts={formData?.fixedCosts || []}
        onAddFixedCost={() => {
          const defaultStartDate = outcomeData?.start_date ? dayjs(outcomeData.start_date).format('YYYY-MM') : 
            formData?.start_month || dayjs().format('YYYY-MM');
          const defaultEndDate = outcomeData?.end_date ? dayjs(outcomeData.end_date).format('YYYY-MM') : 
            defaultStartDate;

          const newCost = {
            id: `fixed-cost-${Date.now()}`,
            type: 'Licenses',
            monthlyCost: 0,
            details: '',
            start_date: defaultStartDate,
            end_date: defaultEndDate,
            milestone_id: '',
            capex_classification: 'OPEX',
            company: '',
            departments: []
          };
          
          updateFormData(prev => ({
            ...prev,
            fixedCosts: [...(prev.fixedCosts || []), newCost]
          }));
        }}
        onRemoveFixedCost={(costId) => {
          updateFormData(prev => ({
            ...prev,
            fixedCosts: (prev.fixedCosts || []).filter(cost => cost.id !== costId)
          }));
        }}
        onFixedCostChange={(costId, field, value) => {
          setIsUpdating(true);
          updateFormData(prev => ({
            ...prev,
            fixedCosts: (prev.fixedCosts || []).map(cost => {
              if (cost.id === costId) {
                const updated = { ...cost, [field]: value };

                if (field === 'start_date' && updated.end_date) {
                  const startDate = dayjs(value + '-01');
                  const endDate = dayjs(updated.end_date + '-01');
                  if (endDate.isBefore(startDate)) {
                    updated.end_date = value;
                  }
                }

                if (field === 'end_date' && updated.start_date) {
                  const startDate = dayjs(updated.start_date + '-01');
                  const endDate = dayjs(value + '-01');
                  if (endDate.isBefore(startDate)) {
                    updated.start_date = value;
                  }
                }

                return updated;
              }
              return cost;
            })
          }));
          setTimeout(() => setIsUpdating(false), 500);
        }}
        formatCurrency={formatCurrency}
        readOnly={readOnly}
        saving={saving}
        deptFilter={selectedDeptFilter}
        departments={departments}
        locationOptions={locationOptions}
      />

      {/* Member Allocation Summary - EXTRACTED */}
      <MemberAllocationTable
        memberAllocationSummary={memberAllocationSummary}
        getAllocationStatus={getAllocationStatus}
        getAllocationColor={getAllocationColor}
        formatCurrency={formatCurrency}
      />

      {/* Modals */}
      <Suspense fallback={<CircularProgress />}>
        {/* Add Role Dialog */}
        <AddRoleDialog
          open={addRoleDialogOpen}
          onClose={() => {
            setAddRoleDialogOpen(false);
            setSelectedMilestoneForRole(null);
          }}
          milestone={selectedMilestoneForRole}
          onAddRole={(newRole) => {
            updateFormData(prev => ({
              ...prev,
              milestones: prev.milestones.map(m => {
                if (m.id === selectedMilestoneForRole.id) {
                  return {
                    ...m,
                    roles: [...(m.roles || []), newRole]
                  };
                }
                return m;
              })
            }));
            setAddRoleDialogOpen(false);
            setSelectedMilestoneForRole(null);
          }}
          locationOptions={locationOptions}
          roleOptions={roleOptions}
          outcomeData={outcomeData}
        />

        {/* Edit Role Dialog */}
        <EditRoleDialog
          open={editRoleDialogOpen}
          onClose={() => {
            setEditRoleDialogOpen(false);
            setEditingRoleMetadata(null);
          }}
          editingRole={editingRoleMetadata}
          onSaveRole={(updatedRoleData) => {
            if (!editingRoleMetadata) return;
            
            const { milestone, role } = editingRoleMetadata;
            
            updateFormData(prev => ({
              ...prev,
              milestones: prev.milestones.map(m => {
                if (m.id === milestone.id) {
                  return {
                    ...m,
                    roles: m.roles.map(r => {
                      if (r.id === role.id) {
                        return { ...r, ...updatedRoleData };
                      }
                      return r;
                    })
                  };
                }
                return m;
              })
            }));
            
            setEditRoleDialogOpen(false);
            setEditingRoleMetadata(null);
          }}
          locationOptions={locationOptions}
          roleOptions={roleOptions}
          outcomeData={outcomeData}
        />

        {/* Role Timeline Modal */}
                <RoleTimelineModal
          open={roleTimelineModalOpen}
          onClose={() => {
            setRoleTimelineModalOpen(false);
            setEditingRole(null);
          }}
          role={editingRole}
          onSave={handleRoleTimelineUpdate}
          readOnly={readOnly}
        />

        {/* Add Member Modal */}
        <AddMemberModal
          open={addMemberModalOpen}
          onClose={() => setAddMemberModalOpen(false)}
          onMemberAdded={async () => {
            // Refresh roster data
            await clearCacheAndRefresh();
            setAddMemberModalOpen(false);
          }}
        />

        {/* Timeline Adjustment Modal */}
        <TimelineAdjustmentModal
          open={timelineModalOpen}
          onClose={() => {
            setTimelineModalOpen(false);
            setPendingTimelineChange(null);
          }}
          oldMonths={pendingTimelineChange?.oldMonths || 0}
          newMonths={pendingTimelineChange?.newMonths || 0}
          onConfirm={handleTimelineAdjustmentConfirm}
        />
      </Suspense>

      {/* Copy Role Menus - EXTRACTED */}
      <CopyRoleMenus
        copyMenuAnchor={copyMenuAnchor}
        roleToCopy={roleToCopy}
        onCopyRoleClose={handleCopyRoleClose}
        onCopyRoleToMilestone={(milestone) => {
          const copiedRole = handleCopyRoleToMilestone(milestone);
          if (copiedRole) {
            // Auto-open timeline editor for copied role
            setTimeout(() => {
              handleEditMilestoneRoleTimeline(milestone, copiedRole);
            }, 600);
          }
        }}
        copyAllRolesMenuAnchor={copyAllRolesMenuAnchor}
        sourceMilestoneForCopy={sourceMilestoneForCopy}
        onCopyAllRolesClose={handleCopyAllRolesClose}
        onCopyAllRolesToMilestone={handleCopyAllRolesToMilestone}
        formData={formData}
      />
      </Box>
    </LocalizationProvider>
  );
}

export default StaffingStep;